import React, { useState, useEffect, useCallback } from "react";
import { Box, Collapse, Typography, useTheme } from "@mui/material";
import { CaretDown, CaretRight } from "@phosphor-icons/react";
import { useDarkMode } from "../../../../hooks/useDarkMode";
import { Folder } from "../../../../util/Types/Folder";
import { UserService } from "../../../../service/UserService";
import { loadFolderWithSubfolders, getAgentsOnly } from "./agentTreeUtils";
import { FlowItem } from "./AgentTreeComponents";

interface FlowTreeViewProps {
  folders: Folder[];
  spaces: any[];
  onFlowSelect: (flow: any) => void;
  searchTerm?: string;
}

export const FlowTreeView: React.FC<FlowTreeViewProps> = ({
  folders,
  spaces,
  onFlowSelect,
  searchTerm = "",
}) => {
  const theme = useTheme();
  const { customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();
  const [expandedSpaces, setExpandedSpaces] = useState<Set<string>>(new Set());
  const [isPrivateExpanded, setIsPrivateExpanded] = useState(false);
  const [loadedFolders, setLoadedFolders] = useState<Folder[]>(folders);
  const [loadedSpaces, setLoadedSpaces] = useState<any[]>(spaces);

  useEffect(() => {
    const loadData = async () => {
      const foldersWithSubfolders = await Promise.all(
        folders.map((folderRef: any) => loadFolderWithSubfolders(folderRef)),
      );
      const loadedFoldersData = foldersWithSubfolders.filter(
        (f): f is Folder => f !== null,
      );
      setLoadedFolders(loadedFoldersData);

      const spacesData = await UserService.getSpaces();
      const spacesWithFolders = await Promise.all(
        (spacesData.data || []).map(async (space: any) => {
          const foldersWithFlows = await Promise.all(
            (space.folder || []).map((folderRef: any) =>
              loadFolderWithSubfolders(folderRef),
            ),
          );
          return {
            ...space,
            folder: foldersWithFlows.filter((f): f is Folder => f !== null),
          };
        }),
      );
      setLoadedSpaces(spacesWithFolders);
    };

    loadData();
  }, [folders, spaces]);

  const handleSpaceToggle = useCallback((spaceId: string) => {
    setExpandedSpaces((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(spaceId)) {
        newExpanded.delete(spaceId);
      } else {
        newExpanded.add(spaceId);
      }
      return newExpanded;
    });
  }, []);

  const textColor = isDarkMode ? palette.gray[100] : palette.gray[900];
  const dividerColor = isDarkMode ? palette.gray[700] : palette.gray[200];
  const hoverBg = isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)";
  const sectionBg = isDarkMode ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)";

  const privateAgents = getAgentsOnly(loadedFolders, []);

  const filteredPrivateAgents = searchTerm
    ? privateAgents.filter((agent) =>
        agent.name?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : privateAgents;

  return (
    <Box
      sx={{
        maxHeight: "500px",
        overflowY: "auto",
        padding: "8px",
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: isDarkMode ? palette.gray[800] : palette.gray[100],
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: isDarkMode ? palette.gray[600] : palette.gray[300],
          borderRadius: "4px",
          "&:hover": {
            backgroundColor: isDarkMode ? palette.gray[500] : palette.gray[400],
          },
        },
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Box
          onClick={() => setIsPrivateExpanded(!isPrivateExpanded)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            cursor: "pointer",
            borderRadius: "6px",
            marginBottom: "8px",
            backgroundColor: sectionBg,
            "&:hover": {
              backgroundColor: hoverBg,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            {isPrivateExpanded ? (
              <CaretDown size={18} color={textColor} weight="bold" />
            ) : (
              <CaretRight size={18} color={textColor} weight="bold" />
            )}
            <Typography
              sx={{
                color: textColor,
                fontSize: "15px",
                fontWeight: 600,
                letterSpacing: "0.01em",
              }}
            >
              Private{" "}
              <Typography
                component="span"
                sx={{
                  color: isDarkMode ? palette.gray[400] : palette.gray[500],
                  fontSize: "15px",
                  fontWeight: 400,
                }}
              >
                (
                {filteredPrivateAgents.length > 0
                  ? filteredPrivateAgents.length
                  : "none"}
                )
              </Typography>
            </Typography>
          </Box>
        </Box>
        <Collapse in={isPrivateExpanded}>
          <Box sx={{ pl: 1, pr: 1 }}>
            {filteredPrivateAgents.map((agent, index) => (
              <FlowItem
                key={agent.id}
                flow={agent}
                onFlowSelect={onFlowSelect}
                isDarkMode={isDarkMode}
                palette={palette}
                isLast={index === filteredPrivateAgents.length - 1}
              />
            ))}
          </Box>
        </Collapse>
        {loadedSpaces.length > 0 && (
          <Box
            sx={{
              height: "1px",
              backgroundColor: dividerColor,
              margin: "12px 0",
            }}
          />
        )}
      </Box>

      {loadedSpaces.map((space, spaceIndex) => {
        const spaceAgents = getAgentsOnly(
          space.folder || [],
          space.flows || [],
        );
        const isExpanded = expandedSpaces.has(space.id);

        const filteredSpaceAgents = searchTerm
          ? spaceAgents.filter((agent) =>
              agent.name?.toLowerCase().includes(searchTerm.toLowerCase()),
            )
          : spaceAgents;

        if (searchTerm && filteredSpaceAgents.length === 0) {
          return null;
        }

        const hasAgents = filteredSpaceAgents.length > 0;

        return (
          <Box
            key={space.id}
            sx={{ mb: spaceIndex < loadedSpaces.length - 1 ? 2 : 0 }}
          >
            <Box
              onClick={
                hasAgents ? () => handleSpaceToggle(space.id) : undefined
              }
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 16px",
                cursor: hasAgents ? "pointer" : "default",
                borderRadius: "6px",
                marginBottom: "8px",
                backgroundColor: sectionBg,
                opacity: hasAgents ? 1 : 0.6,
                "&:hover": {
                  backgroundColor: hasAgents ? hoverBg : sectionBg,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                {hasAgents && (
                  <>
                    {isExpanded ? (
                      <CaretDown size={18} color={textColor} weight="bold" />
                    ) : (
                      <CaretRight size={18} color={textColor} weight="bold" />
                    )}
                  </>
                )}
                <Typography
                  sx={{
                    color: textColor,
                    fontSize: "15px",
                    fontWeight: 600,
                    letterSpacing: "0.01em",
                  }}
                >
                  {space.name}{" "}
                  <Typography
                    component="span"
                    sx={{
                      color: isDarkMode ? palette.gray[400] : palette.gray[500],
                      fontSize: "15px",
                      fontWeight: 400,
                    }}
                  >
                    (
                    {filteredSpaceAgents.length > 0
                      ? filteredSpaceAgents.length
                      : "none"}
                    )
                  </Typography>
                </Typography>
              </Box>
            </Box>
            <Collapse in={isExpanded}>
              <Box sx={{ pl: 1, pr: 1 }}>
                {filteredSpaceAgents.map((agent, index) => (
                  <FlowItem
                    key={agent.id}
                    flow={agent}
                    onFlowSelect={onFlowSelect}
                    isDarkMode={isDarkMode}
                    palette={palette}
                    isLast={index === filteredSpaceAgents.length - 1}
                  />
                ))}
              </Box>
            </Collapse>
            {spaceIndex < loadedSpaces.length - 1 && (
              <Box
                sx={{
                  height: "1px",
                  backgroundColor: dividerColor,
                  margin: "12px 0",
                  mt: 1.5,
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};
